import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Dialog } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import ConfirmDialog from "components/ConfirmDialog";
import DeleteIconButton from "components/IconButtons/DeleteIconButton";
import EditIconButton from "components/IconButtons/EditIconButton";
import LazyTable, { TableCellType } from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useState } from "react";
import { DropResult } from "react-beautiful-dnd";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  IOrganizationCatalogSku,
  ListResponseOfOrganizationCatalogSku,
  OrganizationCatalogSkuRequest,
  OrganizationDetails,
} from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import OrganizationCatalogSkuForm from "./OrganizationCatalogSkuForm";

type OrganizationCatalogSkusProps = {
  organization: OrganizationDetails;
};

const useStyles = (theme: Theme) => ({
  addOrganizationCatalogSkuButton: {
    display: "flex",
    justifyContent: "end",
    width: "100%",
    margin: theme.spacing(4, 0, 3, 0),
  },
  root: {
    width: "100%",
  },
  buttons: {
    display: "flex",
  },
});

const OrganizationCatalogSkus = (props: OrganizationCatalogSkusProps) => {
  const { organization } = props;
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const { catalogSkuService } = useGlobalContext();
  const [updateOpen, updateOpenActions] = useBoolean(false);
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const [
    organizationCatalogSkuToUpdate,
    organizationCatalogSkuToUpdateActions,
  ] = useState<IOrganizationCatalogSku | null>(null);
  const [
    organizationCatalogSkuIdToDelete,
    organizationCatalogSkuIdToDeleteActions,
  ] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const organizationCatalogSkusData = useStrictQuery<ListResponseOfOrganizationCatalogSku>(
    ["OrganizationCatalogSkus", organization.id],
    () =>
      catalogSkuService.getOrganizationCatalogSkus(
        OrganizationCatalogSkuRequest.fromJS({
          pageSize: -1,
          organizationId: organization?.id,
        })
      ),
    {
      keepPreviousData: true,
    }
  );

  let organizationCatalogSkus = organizationCatalogSkusData.data?.data ?? [];

  const onSave = () => {
    updateOpenActions.setFalse();
    organizationCatalogSkuToUpdateActions(null);

    queryClient.invalidateQueries(["OrganizationCatalogSkus", organization.id]);
  };

  const onCancel = () => {
    updateOpenActions.setFalse();
    organizationCatalogSkuToUpdateActions(null);
  };

  const onDelete = async () => {
    await catalogSkuService.deleteOrganizationCatalogSku(
      organizationCatalogSkuIdToDelete!
    );
    deleteOpenActions.setFalse();
    queryClient.invalidateQueries(["OrganizationCatalogSkus", organization.id]);
  };

  const Buttons = ({ value }: { value: IOrganizationCatalogSku }) => {
    const isDisabled = value.organizationId !== organization.id;
    return (
      <Box sx={styles.buttons}>
        <EditIconButton
          disabled={isDisabled}
          onClick={(e) => {
            e.stopPropagation();
            organizationCatalogSkuToUpdateActions(value);
            updateOpenActions.setTrue();
          }}
        />
        <DeleteIconButton
          disabled={isDisabled}
          onClick={(e) => {
            e.stopPropagation();
            deleteOpenActions.setTrue();
            organizationCatalogSkuIdToDeleteActions(value.id);
          }}
        />
      </Box>
    );
  };

  const onDragEndHandler = async (result: DropResult) => {
    if (!result.destination) {
      return;
    }
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "sku",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "skuLabel" }),
    },
    {
      accessor: "skuTitle",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "skuTitleColumnLabel" }),
    },
    {
      accessor: "organizationCatalogSkuType",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "typeLabel" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: Buttons,
      label: "",
      noSort: true,
    },
  ];

  return (
    <Box sx={styles.root}>
      <LazyTable
        {...{
          rowCount: organizationCatalogSkusData.data?.totalRecords ?? 0,
          rows: organizationCatalogSkus.map((x) => {
            const isDisabled = x.organizationId !== organization.id;
            return {
              ...x,
              color: isDisabled ? "#eee" : undefined,
              tooltip: isDisabled
                ? t(
                    { id: "organizationCatalogInheritedTooltip" },
                    { organizationName: x.organizationName }
                  )
                : undefined,
            };
          }),
          loading: organizationCatalogSkusData.isFetching,
          titleComponent: (
            <Box sx={styles.addOrganizationCatalogSkuButton}>
              <Button
                onClick={updateOpenActions.setTrue}
                startIcon={<AddIcon />}
                data-cy="addOrganizationCatalogSku"
              >
                <T id="addOrganizationCatalogSkuButton" />
              </Button>
            </Box>
          ),
          dense: true,
          cells,
          page: 0,
          order: "asc",
          orderBy: "skuTitle",
          rowsPerPage: organizationCatalogSkus.length,
          setPage: () => {},
          setOrder: () => {},
          setOrderBy: () => {},
          setRowsPerPage: () => {},
          allowPagination: false,
          allowDragAndDrop: false,
          onDragAndDropEnd: onDragEndHandler,
        }}
      />

      <Dialog open={updateOpen} maxWidth="sm" fullWidth disableEnforceFocus>
        <OrganizationCatalogSkuForm
          onOrganizationCatalogSkuSaved={onSave}
          onCancel={onCancel}
          organization={organization}
          OrganizationCatalogSku={organizationCatalogSkuToUpdate ?? undefined}
          submitLabel={
            organizationCatalogSkuToUpdate != null
              ? t({ id: "updateOrganizationCatalogSkuButton" })
              : t({ id: "addOrganizationCatalogSkuButton" })
          }
        />
      </Dialog>
      <ConfirmDialog
        dialogTitleKey="deleteOrganizationCatalogSkuTitle"
        contentKey="deleteOrganizationCatalogSkuQuestion"
        open={deleteOpen}
        onCancel={deleteOpenActions.setFalse}
        handleConfirm={onDelete}
      />
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Box>
  );
};

export default withErrorBoundary(OrganizationCatalogSkus);
